import React, {useState, useEffect} from 'react';

import { ActionIcon, Box, Stack, Text } from '@mantine/core';
import {
    IconChevronDown,
    IconPlayerSkipBack,
    IconMusic,
    IconRewindBackward15,
    IconRewindForward15,
} from '@tabler/icons-react';
import { Sheet } from 'react-modal-sheet';
import { useModule } from '@/hooks/useModule';
import { PlayIcon } from '@/modules/audio_player/audio_player_module';
import { AudioSeekBar } from '@/components/AudioSeekBar';
import { useNavigate } from 'react-router';
import { navigateToPathForReason } from '@/modules/navigation/navigation_module';

interface FullPlayerProps {
    isOpen: boolean;
    onClose: () => void;
}

export function FullPlayer({ isOpen, onClose }: FullPlayerProps) {
    const audioPlayerModule = useModule('AudioPlayer');
    const playbackStatus = audioPlayerModule.useAudioPlaybackStatus();
    const navigate = useNavigate();
    const [internalOpen, setInternalOpen] = useState(isOpen);

    useEffect(() => {
        if (isOpen) {
            setInternalOpen(true);
        }
    }, [isOpen]);

    const handleClose = () => {
        setInternalOpen(false);
        setTimeout(() => {
            onClose();
        }, 300);
    };

    const songTitle = playbackStatus?.fileInfo?.name?.replace(/\.[^/.]+$/, '') ?? 'No song playing';

    const handleSongTitleClick = () => {
        if (playbackStatus?.fileInfo) {
            handleClose();
            window.scrollTo(0, 0);
            navigateToPathForReason(
                navigate,
                'navigate_to_file_from_full_player',
                `/workspaces/${playbackStatus.fileInfo.workspace_id}/files/${playbackStatus.fileInfo.id}`
            );
        }
    };

    const handleSeekBackward = () => {
        const audio = (window as unknown as {audioElement?: HTMLAudioElement}).audioElement;
        if (audio) {
            const newTime = Math.max(0, audio.currentTime - 15);
            audio.currentTime = newTime;
        }
    };

    const handleSeekForward = () => {
        const audio = (window as unknown as {audioElement?: HTMLAudioElement}).audioElement;
        if (audio) {
            const newTime = Math.min(audio.duration || 0, audio.currentTime + 15);
            audio.currentTime = newTime;
        }
    };

    const handleRestart = () => {
        const audio = (window as unknown as {audioElement?: HTMLAudioElement}).audioElement;
        if (audio) {
            audio.currentTime = 0;
        }
    };

    return (
        <Sheet
            isOpen={internalOpen}
            onClose={handleClose}
        >
            <Sheet.Container
                style={{
                    backgroundColor: 'var(--mantine-color-body)',
                }}
            >
                <Sheet.Header />
                <Sheet.Content
                    style={{
                        backgroundColor: 'var(--mantine-color-body)',
                    }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: 20,
                            height: '100%',
                        }}
                    >
            <Box style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                <ActionIcon variant="transparent" size="lg" onClick={handleClose}>
                    <IconChevronDown size={32} />
                </ActionIcon>
            </Box>

            <Stack gap="xl" style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <Stack gap="xs" style={{ width: '100%', textAlign: 'center' }}>
                    <Text
                        size="xl"
                        fw={600}
                        onClick={handleSongTitleClick}
                        style={{
                            cursor: playbackStatus?.fileInfo ? 'pointer' : 'default',
                        }}
                    >
                        {songTitle}
                    </Text>
                </Stack>

                <AudioSeekBar showTimeLabels={true} />

                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, width: '100%', maxWidth: 400, margin: '20px auto 0' }}>
                    <ActionIcon
                        variant="transparent"
                        size={50}
                        onClick={handleRestart}
                        disabled={!playbackStatus?.fileInfo}
                    >
                        <IconPlayerSkipBack size={32} />
                    </ActionIcon>

                    <ActionIcon
                        variant="transparent"
                        size={50}
                        onClick={handleSeekBackward}
                        disabled={!playbackStatus?.fileInfo}
                    >
                        <IconRewindBackward15 size={32} />
                    </ActionIcon>

                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                        {playbackStatus?.fileInfo && (
                            <PlayIcon
                                fileInfo={playbackStatus.fileInfo}
                                size={60}
                                style={{
                                    background: 'var(--mantine-color-filled)',
                                    borderRadius: '50%',
                                    padding: 16,
                                }}
                            />
                        )}
                    </Box>

                    <ActionIcon
                        variant="transparent"
                        size={50}
                        onClick={handleSeekForward}
                        disabled={!playbackStatus?.fileInfo}
                    >
                        <IconRewindForward15 size={32} />
                    </ActionIcon>

                    <Box style={{ width: 50, height: 50 }} />
                </Box>
            </Stack>
        </Box>
                </Sheet.Content>
            </Sheet.Container>
            <Sheet.Backdrop onTap={handleClose} />
        </Sheet>
    );
}
